import styled, { keyframes } from 'styled-components'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

export const SettingsContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: calc(100vh - 75px);
  height: 100%;
  margin: 0;
  padding: 0;
  font-family: 'Montserrat', sans-serif;
  color: ${(props) => props.theme.textPrimary};
  box-sizing: border-box;
  background: ${(props) => props.theme.background || '#0b1120'};

  @media (max-width: 900px) {
    flex-direction: column;
  }
`

export const SettingsLayout = styled.div`
  display: flex;
  width: 100%;
  min-height: 100%;
  align-items: stretch;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`

// Aliases de compatibilidade para evitar ReferenceError em hot reloads anteriores
export const TabNav = styled.div`
  display: flex;
  gap: 12px;
`
export const TabItem = styled.button<{ $active?: boolean }>`
  display: flex;
`

export const SettingsSidebar = styled.nav`
  display: flex;
  flex-direction: column;
  width: 290px;
  flex-shrink: 0;
  background: ${(props) => props.theme.cardBackground};
  border-right: 1px solid ${(props) => props.theme.cardBorder};
  border-radius: 0;
  padding: 24px 18px;
  box-sizing: border-box;
  box-shadow: ${(props) => (props.theme.name === 'light' ? '2px 0 16px rgba(0, 31, 92, 0.05)' : '4px 0 24px rgba(0, 0, 0, 0.25)')};

  @media (max-width: 900px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${(props) => props.theme.cardBorder};
  }
`

export const SidebarHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 20px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};

  .brand-group {
    display: flex;
    align-items: center;
    gap: 12px;

    .brand-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 42px;
      height: 42px;
      border-radius: 12px;
      background: linear-gradient(135deg, rgba(245, 158, 11, 0.25), rgba(217, 119, 6, 0.12));
      color: #f59e0b;
      font-size: 22px;
      border: 1px solid rgba(245, 158, 11, 0.35);
      flex-shrink: 0;
    }

    .brand-text {
      display: flex;
      flex-direction: column;

      span {
        font-size: 15px;
        font-weight: 800;
        color: ${(props) => props.theme.titleColor};
        letter-spacing: -0.2px;
      }

      small {
        font-size: 11px;
        font-weight: 600;
        color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#94a3b8')};
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }
  }

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: #94a3b8;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      color: #ffffff;
      border-color: rgba(255, 255, 255, 0.18);
    }
  }
`

export const SidebarNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`

export const SidebarFooter = styled.div`
  margin-top: auto;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  gap: 12px;

  .user-avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f59e0b, #d97706);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 800;
    color: #ffffff;
    flex-shrink: 0;
    border: 2px solid rgba(245, 158, 11, 0.4);
  }

  .user-info {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .name {
      font-size: 13px;
      font-weight: 700;
      color: #ffffff;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    .role {
      font-size: 11px;
      color: #f59e0b;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
`

interface SidebarItemProps {
  $active: boolean
}

export const SidebarItem = styled.button<SidebarItemProps>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  width: 100%;
  text-align: left;
  border-radius: 12px;
  border: 1px solid ${(props) => (props.$active ? 'rgba(245, 158, 11, 0.35)' : 'transparent')};
  background: ${(props) =>
    props.$active
      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.16), rgba(245, 158, 11, 0.06))'
      : 'transparent'};
  border-left: 4px solid ${(props) => (props.$active ? '#f59e0b' : 'transparent')};
  color: ${(props) => (props.$active ? '#ffffff' : '#94a3b8')};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Montserrat', sans-serif;

  .icon-box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${(props) => (props.$active ? 'rgba(245, 158, 11, 0.2)' : 'rgba(255, 255, 255, 0.04)')};
    color: ${(props) => (props.$active ? '#f59e0b' : '#64748b')};
    font-size: 18px;
    flex-shrink: 0;
    transition: all 0.2s ease;
  }

  .text-box {
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow: hidden;

    .title {
      font-size: 14px;
      font-weight: 700;
      color: ${(props) => (props.$active ? '#f59e0b' : props.theme.name === 'light' ? '#0f172a' : '#e2e8f0')};
    }

    .desc {
      font-size: 11px;
      color: ${(props) => (props.$active ? '#d97706' : props.theme.name === 'light' ? '#64748b' : '#64748b')};
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  &:hover {
    background: ${(props) => (props.$active ? 'rgba(245, 158, 11, 0.2)' : props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.04)')};
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};

    .icon-box {
      color: #f59e0b;
      background: rgba(245, 158, 11, 0.15);
    }

    .text-box .title {
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    }
  }
`

export const SettingsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  .title-group {
    h2 {
      font-size: 24px;
      font-weight: 800;
      color: ${(props) => props.theme.titleColor};
      margin: 0 0 6px 0;
      display: flex;
      align-items: center;
      gap: 12px;

      svg {
        color: #f59e0b;
      }
    }

    p {
      font-size: 14px;
      color: ${(props) => props.theme.subtitleColor};
      margin: 0;
    }
  }

  .admin-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    background: rgba(245, 158, 11, 0.12);
    border: 1px solid rgba(245, 158, 11, 0.3);
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    color: #f59e0b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`

export const SettingsMainContent = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 32px 40px;
  box-sizing: border-box;
  overflow-y: auto;
  height: 100%;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`

export const SettingsCard = styled.div`
  background: ${(props) => props.theme.cardBackground};
  border: 1px solid ${(props) => props.theme.cardBorder};
  border-radius: 16px;
  padding: 28px;
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 4px 20px rgba(0, 31, 92, 0.06)' : '0 10px 25px -5px rgba(0, 0, 0, 0.3)')};
  margin-bottom: 24px;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.06)')};

    .header-text {
      h3 {
        font-size: 17px;
        font-weight: 700;
        color: ${(props) => props.theme.titleColor};
        margin: 0 0 4px 0;
        display: flex;
        align-items: center;
        gap: 10px;

        svg {
          color: #38bdf8;
        }
      }

      p {
        font-size: 13px;
        color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#94a3b8')};
        margin: 0;
      }
    }
  }
`

export const StatusPill = styled.span<{ $configured: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: ${(props) => (props.$configured ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)')};
  border: 1px solid ${(props) => (props.$configured ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)')};
  color: ${(props) => (props.$configured ? '#34d399' : '#f87171')};
`

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`

export const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'light' ? '#334155' : '#cbd5e1')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const Input = styled.input`
  width: 100%;
  height: 44px;
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(15, 23, 42, 0.6)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)')};
  border-radius: 10px;
  padding: 0 14px;
  color: ${(props) => props.theme.textPrimary};
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
  transition: all 0.2s ease;
  box-sizing: border-box;
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 2px 6px rgba(0, 31, 92, 0.04)' : 'none')};

  &:focus {
    outline: none;
    border-color: #f59e0b;
    background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(15, 23, 42, 0.8)')};
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
  }

  &::placeholder {
    color: ${(props) => (props.theme.name === 'light' ? '#94a3b8' : '#64748b')};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  input {
    padding-right: 44px;
  }

  .toggle-btn {
    position: absolute;
    right: 12px;
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    padding: 4px;

    &:hover {
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    }
  }
`

export const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #f59e0b;
  }

  label {
    font-size: 13px;
    color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#cbd5e1')};
    cursor: pointer;
    user-select: none;
  }
`

export const TestPanel = styled.div`
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(56, 189, 248, 0.2);
  border-radius: 12px;
  padding: 20px;
  margin-top: 10px;
  margin-bottom: 24px;

  .test-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: #38bdf8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 12px;
  }

  .test-row {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;

    input {
      flex: 1;
      min-width: 250px;
    }
  }
`

export const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'accent' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 24px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  white-space: nowrap;

  ${(props) => {
    switch (props.$variant) {
      case 'accent':
        return `
          background: linear-gradient(135deg, #0284c7, #0369a1);
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(2, 132, 199, 0.3);
          &:hover:not(:disabled) {
            background: linear-gradient(135deg, #0369a1, #075985);
            transform: translateY(-1px);
          }
        `
      case 'secondary':
        return `
          background: rgba(255, 255, 255, 0.08);
          color: #e2e8f0;
          border: 1px solid rgba(255, 255, 255, 0.12);
          &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.15);
          }
        `
      case 'primary':
      default:
        return `
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: #ffffff;
          box-shadow: 0 4px 14px rgba(245, 158, 11, 0.3);
          &:hover:not(:disabled) {
            background: linear-gradient(135deg, #d97706, #b45309);
            transform: translateY(-1px);
          }
        `
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .spinner {
    animation: ${spin} 1s linear infinite;
  }
`

export const AiFeatureNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.05));
  border: 1px solid rgba(168, 85, 247, 0.25);
  border-radius: 16px;
  padding: 28px;
  position: relative;
  overflow: hidden;

  .glow-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(168, 85, 247, 0.2);
    border: 1px solid rgba(168, 85, 247, 0.4);
    color: #c084fc;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    padding: 4px 12px;
    border-radius: 999px;
    width: fit-content;
  }

  h3 {
    font-size: 20px;
    font-weight: 800;
    color: #ffffff;
    margin: 0;
  }

  p {
    font-size: 14px;
    color: #cbd5e1;
    line-height: 1.6;
    margin: 0;
    max-width: 750px;
  }
`
